import './AddonPackageDialog.scss';

import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import CloseIcon from '@mui/icons-material/Close';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';

import { FormGrid, FormRow } from 'app/components/FormGrid/FormGrid';
import CheckboxGroup from 'app/components/CheckboxGroup/CheckboxGroup';
import RadioGroup from 'app/components/RadioGroup/RadioGroup';
import RichTextEditor from 'app/components/RichTextEditor/RichTextEditor';
import UnigineButton from 'app/components/UnigineButton/UnigineButton';

import useAppSelector from 'app/hooks/useAppSelector';
import { platformNames, regExps } from 'app/configs/appConfig';
import { formFieldErrors, tooltips } from 'app/utils/constants/contentConstants';

import { selectVersions } from 'app/main/sections/Addons/store/versionsSlice';
import { selectPlatform } from 'app/main/sections/Addons/store/platformSlice';
import { selectSourceLanguages } from 'app/main/sections/Addons/store/sourceLanguagesSlice';
import { IPackageData } from 'app/main/sections/PublisherPanel/sections/AddonSection/store/baseInfoSlice';
import IPackage from 'app/interfaces/addons/IPackage';

interface IAddonUpdatePackageDialog {
  editingPackage: IPackage | null;
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (data: IPackageData) => void;
}

const AddonUpdatePackageDialog: FC<IAddonUpdatePackageDialog> = ({
  onClose,
  isOpen,
  onSubmit,
  isSubmitting,
  editingPackage,
}) => {
  const versionsData = useAppSelector(selectVersions);
  const platformData = useAppSelector(selectPlatform);
  const platformOptions = useMemo(
    () => platformData?.map((platform) => ({ ...platform, label: platformNames[platform.value] })),
    [platformData]
  );
  const sourceLanguagesData = useAppSelector(selectSourceLanguages);

  const defaultValues = {
    sdkVersions: editingPackage?.sdkVersions.map((sdkVersion) => sdkVersion.id) || [],
    description: editingPackage?.description || '',
    platformId: editingPackage?.platform.id || '',
    sourceCodeId: editingPackage?.sourceCode.id || '',
  };

  const { control, handleSubmit } = useForm({
    defaultValues,
    mode: 'onSubmit',
  });

  if (!sourceLanguagesData || !platformOptions) {
    return (
      <Dialog open={isOpen} fullWidth maxWidth="md" onClose={onClose} className="dialog">
        <DialogContent className="dialog-package__content">
          <CircularProgress color="inherit" />

          <IconButton aria-label="close" className="dialog__close-button" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} fullWidth maxWidth="md" onClose={onClose} className="dialog" disableEnforceFocus>
      <DialogContent className="dialog-package__content">
        <h2 className="dialog-package__title">Edit Package Info</h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGrid>
            <FormRow required label="Supported versions">
              <Controller
                control={control}
                name="sdkVersions"
                rules={{
                  validate: (value) => value.length > 0 || 'Please select at least one value', // these black magic APIs I swear
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                  return (
                    <CheckboxGroup
                      items={versionsData || []}
                      selectedValues={value}
                      onChange={onChange}
                      getLabel={(item) => item.value}
                      getValue={(item) => item.id}
                      error={error}
                    />
                  );
                }}
              />
            </FormRow>

            <FormRow required label="Platform">
              <Controller
                control={control}
                name="platformId"
                defaultValue={platformOptions[0].id}
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                  return (
                    <RadioGroup
                      options={platformOptions}
                      selectedValue={value}
                      getValue={(item) => item.id}
                      onChange={onChange}
                      error={error}
                    />
                  );
                }}
              />
            </FormRow>

            {sourceLanguagesData && (
              <FormRow required label="Source code">
                <Controller
                  control={control}
                  name="sourceCodeId"
                  defaultValue={sourceLanguagesData[0].id}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      className="add-on-form__select"
                      value={value}
                      onChange={onChange}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#2a323b',
                            color: '#eff3f7',
                          },
                        },
                      }}
                    >
                      {sourceLanguagesData.map((lang) => (
                        <MenuItem value={lang.id} key={lang.id}>
                          {lang.value}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormRow>
            )}

            <FormRow label="Description" tooltip={tooltips.addonForm.sysreq}>
              <Controller
                control={control}
                name="description"
                rules={{
                  maxLength: {
                    value: 1000,
                    message: formFieldErrors.richTextLength,
                  },
                  pattern: {
                    value: regExps.richText,
                    message: formFieldErrors.richTextCharacters,
                  },
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <RichTextEditor initialValue={value} onChange={onChange} error={error} />

                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </>
                )}
              />
            </FormRow>
          </FormGrid>
          <div className="dialog-package__button-container">
            <UnigineButton
              type="submit"
              className="dialog-package__button dialog-package__button--submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress color="inherit" size={16} /> : 'Save Changes'}
            </UnigineButton>
          </div>
        </form>
        <IconButton aria-label="close" className="dialog__close-button" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
};

export default AddonUpdatePackageDialog;
